import clsx from "clsx";
import { ReactNode } from "react";

import { Tooltip } from "@hagerty/react-components";
import { ChevronRight } from "@hagerty/icons";

import "./StackableButton.scss";

type StackableButtonProps = {
  /** Custom CSS class to apply to this component */
  className?: string;

  /** Optional text to display above the button text */
  label?: string;

  /** Defines the color of the label text */
  labelStyle?: "neutral" | "success" | "error";

  /** Required. This is the main content of the button */
  text: string;

  /** When true long text will not wrap and be truncated */
  nowrapText?: boolean;

  /** Aria-label to apply to the button. If undefined, the button text is used */
  ariaLabel?: string;

  /** Optional element to display under the button text */
  footer?: ReactNode;

  /** If provided, a tooltip icon will render next to the label. The tooltip popup will contain this text */
  tooltipText?: string;

  /** A link to redirect to when the button is clicked */
  href?: string;

  /** A function that is called when the button is clicked */
  onClick?: () => void;
};

/**
 * One or more `StackableButton` components should be rendered within a `StackableButtonGroup`
 */
export const StackableButton = ({
  className,
  label,
  labelStyle = "neutral",
  text,
  nowrapText,
  ariaLabel,
  footer,
  tooltipText,
  href,
  onClick,
}: StackableButtonProps) => {
  const baseClass = "ap-stackable-button";
  const hasAction = !!href || !!onClick;
  const buttonAriaLabel = ariaLabel || text;

  // Determine whether click event was for tooltip or button.
  // Inspired by https://dev.azure.com/hagerty/Hagerty/_git/hagerty-web?path=/packages/core/src/hooks/useOnClickOutside/useOnClickOutside.ts
  const hasTooltipParent = (element: Element): boolean => {
    let el: Element | null = element;

    while (el) {
      // Check for tooltip container class
      if (el?.classList.contains(`${baseClass}__label__tooltip`)) {
        return true;
      }

      // If element has '__content' class then we can stop parent lookup for tooltip.
      if (el?.classList.contains(`${baseClass}__content`)) {
        return false;
      }

      el = el.parentElement;
    }

    return false;
  };

  const onButtonClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    if (!href) {
      event.preventDefault();
    }

    if (hasTooltipParent(event.target as Element)) {
      event.preventDefault();
      return;
    }

    onClick?.();
  };

  const buttonContent = (
    <div className={`${baseClass}__content`}>
      {label && (
        <div className={`${baseClass}__label`}>
          <div className={`${baseClass}__label--${labelStyle}`}>{label}</div>
          {!!tooltipText && (
            <div className={`${baseClass}__label__tooltip`}>
              <Tooltip content={tooltipText} />
            </div>
          )}
        </div>
      )}

      <div
        className={clsx(
          `${baseClass}__text`,
          nowrapText && `${baseClass}__text--nowrap`
        )}
      >
        {text}
      </div>

      {footer && <div className={`${baseClass}__footer`}>{footer}</div>}
    </div>
  );

  if (!hasAction) {
    return (
      <div className={clsx(baseClass, className)} aria-label={buttonAriaLabel}>
        {buttonContent}
      </div>
    );
  }

  return (
    <a
      className={clsx(baseClass, `${baseClass}--action`, className)}
      aria-label={buttonAriaLabel}
      tabIndex={0}
      href={href}
      onClick={(e) => onButtonClick(e)}
    >
      {buttonContent}
      <div className={`${baseClass}__chevron`}>
        <ChevronRight />
      </div>
    </a>
  );
};
